/**
 * Transport — pluggable real-time messaging layer.
 *
 * Two implementations:
 *   - LocalTransport (default): BroadcastChannel — works between tabs on the
 *     same machine. Perfect for testing and same-device co-op.
 *   - WebSocketTransport: connects to the heirloom-server on OVH for true
 *     device-to-device play. (Built later.)
 *
 * Both expose the same API:
 *   transport.connect(roomCode, role)
 *   transport.on(type, handler)
 *   transport.send(type, payload)
 *   transport.disconnect()
 *   transport.myId  // unique id for this client
 */

class LocalTransport {
  constructor() {
    this.myId = `p_${Math.random().toString(36).slice(2, 8)}`;
    this.channel = null;
    this.roomCode = null;
    this.role = null;
    this._handlers = {};
  }

  connect(roomCode, role) {
    if (this.channel) this.disconnect();
    this.roomCode = roomCode;
    this.role = role;
    this.channel = new BroadcastChannel(`heirloom_room_${roomCode}`);
    this.channel.addEventListener('message', (ev) => this._receive(ev.data));
    // Announce presence
    this.send('hello', { role });
  }

  disconnect() {
    if (this.channel) {
      this.send('bye', {});
      this.channel.close();
      this.channel = null;
    }
  }

  on(type, handler) {
    if (!this._handlers[type]) this._handlers[type] = [];
    this._handlers[type].push(handler);
  }

  off(type, handler) {
    if (!this._handlers[type]) return;
    this._handlers[type] = this._handlers[type].filter(h => h !== handler);
  }

  send(type, payload) {
    if (!this.channel) return;
    const msg = { from: this.myId, type, payload, t: Date.now() };
    this.channel.postMessage(msg);
  }

  _receive(msg) {
    if (!msg || msg.from === this.myId) return; // ignore our own
    const handlers = this._handlers[msg.type] || [];
    handlers.forEach(h => h(msg.payload, msg.from));
    // Also fire wildcard
    const all = this._handlers['*'] || [];
    all.forEach(h => h(msg.type, msg.payload, msg.from));
  }
}

// ─── WebSocket transport — for cross-device co-op ──────────────────
class WSTransport {
  constructor(url) {
    this.url = url;
    this.ws = null;
    this.myId = null;
    this.roomCode = null;
    this.role = null;
    this._handlers = {};
    this._queue = [];
    this._connected = false;
  }

  connect(roomCode, role) {
    if (this.ws) this.disconnect();
    this.roomCode = roomCode;
    this.role = role;
    const sep = this.url.includes('?') ? '&' : '?';
    this.ws = new WebSocket(`${this.url}${sep}room=${encodeURIComponent(roomCode)}&role=${encodeURIComponent(role)}`);

    this.ws.addEventListener('open', () => {
      this._connected = true;
      // Flush queued messages
      while (this._queue.length) {
        try { this.ws.send(this._queue.shift()); } catch (_) {}
      }
      // Announce hello to mirror LocalTransport semantics
      this.send('hello', { role });
    });

    this.ws.addEventListener('message', (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch (_) { return; }
      if (msg.type === '__welcome') { this.myId = msg.clientId; return; }
      if (msg.type === '__peer_joined' || msg.type === '__peer_left') {
        const handlers = this._handlers[msg.type] || [];
        handlers.forEach(h => h(msg));
        return;
      }
      const handlers = this._handlers[msg.type] || [];
      handlers.forEach(h => h(msg.payload, msg.from));
    });

    this.ws.addEventListener('close', () => { this._connected = false; this.ws = null; });
    this.ws.addEventListener('error', () => { /* swallow */ });
  }

  disconnect() {
    if (this.ws) {
      try { this.send('bye', {}); } catch (_) {}
      this.ws.close();
      this.ws = null;
    }
    this._connected = false;
  }

  on(type, handler) {
    if (!this._handlers[type]) this._handlers[type] = [];
    this._handlers[type].push(handler);
  }

  off(type, handler) {
    if (!this._handlers[type]) return;
    this._handlers[type] = this._handlers[type].filter(h => h !== handler);
  }

  send(type, payload) {
    const msg = JSON.stringify({ type, payload, t: Date.now() });
    if (this._connected && this.ws) {
      try { this.ws.send(msg); } catch (_) {}
    } else {
      this._queue.push(msg);
    }
  }
}

// Pick the right transport based on URL flag
//   default: BroadcastChannel (works between tabs on same machine)
//   ?ws=1 forces WSS to OVH (for multi-device testing from localhost)
//   non-localhost deploys: always WSS to OVH
function createTransport() {
  const params = new URLSearchParams(window.location.search);
  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const forceWS = params.get('ws') === '1';
  const useWS = forceWS || !isLocal;
  if (useWS) {
    // Deployed endpoint: wss://farm-api.jacobterrell.dev/heirloom/ws
    // (served by nginx subpath proxy on OVH — heirloom-server on :3012)
    const wsBase = 'wss://farm-api.jacobterrell.dev/heirloom/ws';
    return new WSTransport(wsBase);
  }
  return new LocalTransport();
}

export const Transport = createTransport();
